import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, ExternalLink, Youtube, MapPin, CalendarDays, Users,
  Briefcase, Scale, BookOpen, Info, Check, X, ArrowLeft, Trophy,
} from "lucide-react";

const GOLD = "#C9A961";
const RED = "#D90429";
const FQJJB_URL = "https://jjbqc.org";

/* ─────────────────────────────────────────────────────────────
   Colonnes officielles (âge / ceinture) — FQJJB
   ───────────────────────────────────────────────────────────── */
const COLUMNS = [
  { key: "atome", short: "Atome", age: "4–6 ans" },
  { key: "peewee", short: "Pee-Wee", age: "7–9 ans" },
  { key: "junior", short: "Junior", age: "10–12 ans" },
  { key: "ado", short: "Ado", age: "13–15 ans" },
  { key: "blanche", short: "Blanche", age: "16-17 · Adulte/Master · débutant" },
  { key: "bleue", short: "Bleue / Violette", age: "Adulte/Master · intermédiaire" },
  { key: "master_bn", short: "Marron / Noire (Master)", age: "avancé master" },
  { key: "adulte_bn", short: "Marron / Noire (Adulte)", age: "avancé adulte" },
];

// Chaque technique : tableau de 8 booléens alignés sur COLUMNS.
// true = permis (vert) · false = interdit (rouge).
// GI = les 2 dernières colonnes fusionnées (marron/noire). NO-GI = 8 colonnes distinctes.
const T = (a, b, c, d, e, f, g, h) => [a, b, c, d, e, f, g, h];

const NOGI_TABLE = [
  {
    group: "Clés articulaires",
    rows: [
      { name: "Clé de bras droite", cells: T(0, 1, 1, 1, 1, 1, 1, 1) },
      { name: "Americana", cells: T(0, 0, 1, 1, 1, 1, 1, 1) },
      { name: "Kimura", cells: T(0, 0, 1, 1, 1, 1, 1, 1) },
      { name: "Écartement de jambes", cells: T(0, 0, 0, 1, 1, 1, 1, 1) },
      { name: "Clé de cheville dans l'axe (straight ankle lock)", cells: T(0, 0, 0, 1, 1, 1, 1, 1) },
      { name: "Omoplata / Gogoplata", cells: T(0, 0, 0, 1, 1, 1, 1, 1) },
      { name: "Clé de poignet", cells: T(0, 0, 0, 0, 1, 1, 1, 1) },
      { name: "Clé de genou", cells: T(0, 0, 0, 0, 0, 1, 1, 1) },
      { name: "Compression du biceps et du mollet (slicer)", cells: T(0, 0, 0, 0, 0, 1, 1, 1) },
      { name: "Kimura de cheville (toehold)", cells: T(0, 0, 0, 0, 0, 0, 1, 1) },
      { name: "Clé de cheville dans l'axe — torsion vers la jambe qui ne subit pas l'attaque", cells: T(0, 0, 0, 0, 0, 0, 1, 1) },
      { name: "Clés avec torsion des genoux", cells: T(0, 0, 0, 0, 0, 0, 0, 1) },
      { name: "Clé de talon (heel hook)", cells: T(0, 0, 0, 0, 0, 0, 0, 1) },
      { name: "Aoki lock", cells: T(0, 0, 0, 0, 0, 0, 0, 1) },
      { name: "Kimura de cheville vers l'extérieur", cells: T(0, 0, 0, 0, 0, 0, 0, 1) },
      { name: "Torsion du genou", cells: T(0, 0, 0, 0, 0, 0, 0, 0) },
      { name: "Clé sur la colonne vertébrale sans étranglement", cells: T(0, 0, 0, 0, 0, 0, 0, 0) },
      { name: "Torsion du cou et clés cervicales", cells: T(0, 0, 0, 0, 0, 0, 0, 0) },
    ],
  },
  {
    group: "Étranglements",
    rows: [
      { name: "Étranglement arrière (rear naked choke)", cells: T(0, 0, 1, 1, 1, 1, 1, 1) },
      { name: "Triangle (jambes)", cells: T(0, 0, 1, 1, 1, 1, 1, 1) },
      { name: "Ezekiel", cells: T(0, 0, 0, 1, 1, 1, 1, 1) },
      { name: "Guillotine", cells: T(0, 0, 0, 1, 1, 1, 1, 1) },
      { name: "Tirer la tête dans le triangle", cells: T(0, 0, 0, 1, 1, 1, 1, 1) },
      { name: "Triangle (bras)", cells: T(0, 0, 0, 1, 1, 1, 1, 1) },
      { name: "Compression des côtes, mise en garde fermée", cells: T(0, 0, 0, 0, 1, 1, 1, 1) },
    ],
  },
  {
    group: "Projections",
    rows: [
      { name: "Projection par-dessus la tête (tomoe nage)", cells: T(0, 0, 0, 1, 1, 1, 1, 1) },
      { name: "Projection tête et bras (koshiguruma)", cells: T(0, 0, 0, 1, 1, 1, 1, 1) },
      { name: "Seio nage à deux bras", cells: T(0, 0, 0, 1, 1, 1, 1, 1) },
      { name: "Ciseaux de jambes (kanibasami)", cells: T(0, 0, 0, 0, 0, 0, 0, 0) },
      { name: "Projeter l'adversaire sur la tête en contrôlant la colonne (single leg défendu)", cells: T(0, 0, 0, 0, 0, 0, 0, 0) },
      { name: "Slam", cells: T(0, 0, 0, 0, 0, 0, 0, 0) },
      { name: "Suplex en projetant la tête ou la nuque de l'adversaire au sol", cells: T(0, 0, 0, 0, 0, 0, 0, 0) },
    ],
  },
  {
    group: "Comportement général (interdit à tous les niveaux)",
    rows: [
      { name: "Frapper", cells: T(0, 0, 0, 0, 0, 0, 0, 0) },
      { name: "Insulter / jurer", cells: T(0, 0, 0, 0, 0, 0, 0, 0) },
      { name: "Mordre", cells: T(0, 0, 0, 0, 0, 0, 0, 0) },
      { name: "Plier ou tordre les doigts", cells: T(0, 0, 0, 0, 0, 0, 0, 0) },
      { name: "Tirer les cheveux", cells: T(0, 0, 0, 0, 0, 0, 0, 0) },
    ],
  },
];

// GI : mêmes techniques, ajout des étranglements au kimono, colonnes marron/noire fusionnées.
const GI_TABLE = [
  {
    group: "Clés articulaires",
    rows: NOGI_TABLE[0].rows,
  },
  {
    group: "Étranglements",
    rows: [
      { name: "Arc et flèche (étranglement au col)", cells: T(0, 0, 1, 1, 1, 1, 1, 1) },
      { name: "Étranglement au collet (croix / col)", cells: T(0, 0, 1, 1, 1, 1, 1, 1) },
      ...NOGI_TABLE[1].rows,
    ],
  },
  { group: "Projections", rows: NOGI_TABLE[2].rows },
  { group: "Comportement général (interdit à tous les niveaux)", rows: NOGI_TABLE[3].rows },
];

const WEIGHTS = [
  { div: "Atome (4–6 ans)", kg: ["15", "18", "20", "24", "26", "29", "32", "35", "+35"] },
  { div: "Pee-Wee (7–9 ans)", kg: ["21", "24", "27", "30", "33", "36", "39", "42", "+42"] },
  { div: "Junior (10–12 ans)", kg: ["30", "33", "36", "39", "42", "45", "48", "51", "+51"] },
  { div: "Ado (13–15 ans)", kg: ["40", "44", "48", "52", "56", "60", "65", "69", "+69"] },
];

const EVENTS = [
  { name: "ADCC Canada – Montréal Open 2026", date: "26 septembre 2026", city: "Montréal", url: `${FQJJB_URL}/listing/adcc-canada-montreal-open-2026/` },
  { name: "Gatineau Open – Annual Tournament 2026", date: "19 septembre 2026", city: "400 Chemin Fraser, Gatineau", url: `${FQJJB_URL}/listing/gatineau-open-annual-tournament-2026/` },
  { name: "Grapplers League – Québec City Open", date: "14 novembre 2026", city: "1500 Rue de la Rive-Boisée Sud, Québec", url: `${FQJJB_URL}/listing/grapplers-league-14-novembre-2026-ville-de-quebec-quebec-city-open/` },
];

const VIDEOS = [
  { id: "1Fn8xvbcUeQ", title: "IBJJF — Techniques légales & illégales (par âge et ceinture)" },
  { id: "xhUO4XcGkAY", title: "IBJJF — Règles du « leg reap » (Gi et No-Gi)" },
  { id: "nTT-OrtbOwU", title: "IBJJF — Fautes graves (mise à jour des règles)" },
];

function Cell({ ok }) {
  return (
    <td className="border border-white/10 text-center p-0">
      <div
        className="w-full h-8 flex items-center justify-center"
        style={{ background: ok ? "rgba(16,185,129,0.22)" : "rgba(217,4,41,0.22)" }}
      >
        {ok ? <Check size={14} className="text-emerald-400" /> : <X size={13} className="text-[#ff6b83]" />}
      </div>
    </td>
  );
}

function SubmissionTable({ mode }) {
  // mode: "gi" | "nogi". En GI on fusionne les 2 dernières colonnes.
  const table = mode === "gi" ? GI_TABLE : NOGI_TABLE;
  const cols = mode === "gi" ? COLUMNS.slice(0, 6).concat([{ key: "bn", short: "Marron / Noire", age: "avancé" }]) : COLUMNS;
  const colCount = cols.length;

  return (
    <div className="overflow-x-auto rounded-xl border border-white/10 bg-black/40" data-testid={`fqjjb-table-${mode}`}>
      <table className="w-full text-xs min-w-[820px] border-collapse">
        <thead>
          <tr className="bg-white/[0.04]">
            <th className="text-left p-2 border border-white/10 min-w-[240px] sticky left-0 bg-[#0e0e0e] z-10">Technique</th>
            {cols.map((c) => (
              <th key={c.key} className="p-2 border border-white/10 align-bottom" style={{ minWidth: 84 }}>
                <div className="font-bold text-[11px] leading-tight">{c.short}</div>
                <div className="text-[9px] text-white/45 mt-0.5 font-normal">{c.age}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.map((section) => (
            <React.Fragment key={section.group}>
              <tr>
                <td colSpan={colCount + 1} className="bg-[#C9A961]/15 text-[#C9A961] font-bold uppercase tracking-wider text-[10px] p-2 border border-white/10">
                  {section.group}
                </td>
              </tr>
              {section.rows.map((row) => {
                // En GI : fusion des colonnes 7 et 8 (permis si l'une des deux l'est).
                const cells = mode === "gi"
                  ? row.cells.slice(0, 6).concat([row.cells[6] || row.cells[7] ? 1 : 0])
                  : row.cells;
                return (
                  <tr key={row.name} className="hover:bg-white/[0.03]">
                    <td className="p-2 border border-white/10 text-white/80 sticky left-0 bg-[#0c0c0c] z-10">{row.name}</td>
                    {cells.map((ok, i) => <Cell key={i} ok={!!ok} />)}
                  </tr>
                );
              })}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const SectionTitle = ({ icon: Icon, kicker, title }) => (
  <div className="mb-6">
    <div className="flex items-center gap-2 mb-2">
      <Icon size={18} style={{ color: GOLD }} />
      <span className="kz-mono text-[11px] tracking-[0.3em] uppercase" style={{ color: GOLD }}>{kicker}</span>
    </div>
    <h2 className="text-2xl sm:text-3xl font-black tracking-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{title}</h2>
  </div>
);

export default function FQJJBPublic() {
  const [mode, setMode] = useState("gi");

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#fafafa]" data-testid="fqjjb-public">
      {/* HERO */}
      <header className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundImage: `radial-gradient(circle at 80% 20%, ${GOLD}18 0%, transparent 45%)` }} />
        <div className="relative max-w-6xl mx-auto px-6 py-14">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors mb-8" data-testid="fqjjb-back">
            <ArrowLeft size={15} /> Retour à l'accueil
          </Link>
          <div className="chip mb-5 inline-flex items-center gap-2 border border-white/20 px-3 py-1 text-xs uppercase tracking-[0.15em]" style={{ color: GOLD }}>
            <Scale size={13} /> Référence officielle · FQJJB
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] max-w-3xl" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            Tableau maître officiel <span style={{ color: RED }}>FQJJB</span>
          </h1>
          <p className="mt-5 text-base md:text-lg text-white/70 max-w-2xl leading-relaxed">
            Fédération Québécoise de Jiu-Jitsu Brésilien — soumissions permises par âge et ceinture (Gi & No-Gi),
            catégories de poids, clubs, événements, open-mats, emplois et tutoriels de règles IBJJF.
          </p>
          <a href={FQJJB_URL} target="_blank" rel="noopener noreferrer" data-testid="fqjjb-official-link"
            className="mt-7 inline-flex items-center gap-2 bg-[var(--g)] text-black px-5 py-2.5 text-sm font-bold uppercase tracking-wide hover:bg-white transition-colors" style={{ "--g": GOLD }}>
            Site officiel jjbqc.org <ExternalLink size={15} />
          </a>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-14 space-y-20">
        {/* SOUMISSIONS PERMISES */}
        <section data-testid="fqjjb-submissions">
          <SectionTitle icon={ShieldCheck} kicker="Soumissions permises" title="Techniques permises par catégorie" />

          <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4 mb-6 flex gap-3" data-testid="fqjjb-disclaimer">
            <Info size={18} className="text-amber-400 shrink-0 mt-0.5" />
            <p className="text-sm text-white/75 leading-relaxed">
              Reproduction de référence à des fins pédagogiques. En cas de doute, <b>la version officielle de la FQJJB fait foi</b> —
              consultez le tableau officiel sur <a href={FQJJB_URL} target="_blank" rel="noopener noreferrer" className="underline" style={{ color: GOLD }}>jjbqc.org</a>.
              Vert = permis · Rouge = interdit.
            </p>
          </div>

          <div className="inline-flex rounded-lg border border-white/15 p-1 mb-5" data-testid="fqjjb-mode-toggle">
            {[["gi", "Gi (Kimono)"], ["nogi", "No-Gi"]].map(([k, label]) => (
              <button key={k} onClick={() => setMode(k)}
                data-testid={`fqjjb-mode-${k}`}
                className={`px-5 py-2 rounded-md text-sm font-semibold transition ${mode === k ? "text-black" : "text-white/60 hover:text-white"}`}
                style={mode === k ? { background: GOLD } : {}}>
                {label}
              </button>
            ))}
          </div>

          <SubmissionTable mode={mode} />
        </section>

        {/* CATÉGORIES DE POIDS */}
        <section data-testid="fqjjb-weights">
          <SectionTitle icon={Scale} kicker="Catégories de poids" title="Divisions — moins de 16 ans" />
          <div className="grid sm:grid-cols-2 gap-4">
            {WEIGHTS.map((w) => (
              <div key={w.div} className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid={`weight-${w.div}`}>
                <div className="font-bold mb-3" style={{ color: GOLD }}>{w.div}</div>
                <div className="flex flex-wrap gap-2">
                  {w.kg.map((k) => (
                    <span key={k} className="px-2.5 py-1 rounded-md bg-black/50 border border-white/10 text-xs font-mono text-white/80">{k} kg</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ÉVÉNEMENTS */}
        <section data-testid="fqjjb-events">
          <SectionTitle icon={CalendarDays} kicker="Événements" title="Compétitions & tournois FQJJB" />
          <div className="space-y-3">
            {EVENTS.map((e) => (
              <a key={e.name} href={e.url} target="_blank" rel="noopener noreferrer"
                data-testid="fqjjb-event-card"
                className="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.03] p-5 hover:border-[var(--g)]/50 transition-colors" style={{ "--g": GOLD }}>
                <div>
                  <div className="font-bold text-lg" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{e.name}</div>
                  <div className="text-sm text-white/60 mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
                    <span className="inline-flex items-center gap-1.5"><CalendarDays size={13} /> {e.date}</span>
                    <span className="inline-flex items-center gap-1.5"><MapPin size={13} /> {e.city}</span>
                  </div>
                </div>
                <ExternalLink size={16} className="text-white/40 shrink-0" />
              </a>
            ))}
          </div>
        </section>

        {/* CLUBS / OPEN-MATS / EMPLOIS */}
        <section data-testid="fqjjb-directory">
          <SectionTitle icon={Users} kicker="Répertoire" title="Clubs, open-mats & emplois" />
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { icon: MapPin, title: "Clubs & écoles", desc: "Trouve une école affiliée près de chez toi partout au Québec.", cta: "Explorer les écoles", href: `${FQJJB_URL}/#` },
              { icon: Users, title: "Open-mats", desc: "Sessions d'entraînement libres ouvertes à toutes les écoles.", cta: "Voir les open-mats", href: FQJJB_URL },
              { icon: Briefcase, title: "Emplois", desc: "Postes d'instructeurs et opportunités dans le réseau BJJ.", cta: "Voir les emplois", href: FQJJB_URL },
            ].map((c) => (
              <div key={c.title} className="rounded-xl border border-white/10 bg-white/[0.03] p-6 flex flex-col" data-testid={`directory-${c.title}`}>
                <c.icon size={22} style={{ color: GOLD }} />
                <div className="font-bold text-lg mt-3" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{c.title}</div>
                <p className="text-sm text-white/60 mt-2 flex-1">{c.desc}</p>
                <a href={c.href} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold hover:underline" style={{ color: GOLD }}>
                  {c.cta} <ExternalLink size={13} />
                </a>
              </div>
            ))}
          </div>
        </section>

        {/* TUTORIELS IBJJF */}
        <section data-testid="fqjjb-videos">
          <SectionTitle icon={Youtube} kicker="Tutoriels de règles" title="Vidéos officielles IBJJF" />
          <div className="grid md:grid-cols-3 gap-5">
            {VIDEOS.map((v) => (
              <div key={v.id} className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden" data-testid={`fqjjb-video-${v.id}`}>
                <div className="aspect-video bg-black">
                  <iframe
                    className="w-full h-full"
                    src={`https://www.youtube.com/embed/${v.id}`}
                    title={v.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
                <div className="p-4 text-sm font-medium text-white/80">{v.title}</div>
              </div>
            ))}
          </div>
          <a href="https://www.youtube.com/playlist?list=PLndFOMjO-W27bcQWzJbHMhul7llTclJpB" target="_blank" rel="noopener noreferrer"
            className="mt-5 inline-flex items-center gap-2 text-sm font-semibold hover:underline" style={{ color: GOLD }} data-testid="fqjjb-playlist-link">
            <Youtube size={16} /> Voir toute la playlist « Rules Tutorials » IBJJF <ExternalLink size={13} />
          </a>
        </section>

        {/* RÈGLES — résumé */}
        <section data-testid="fqjjb-rules">
          <SectionTitle icon={BookOpen} kicker="Règlements" title="Principes clés du règlement" />
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              "La progression des soumissions permises suit l'âge ET la ceinture : plus le pratiquant est jeune/débutant, plus la liste de techniques autorisées est restreinte.",
              "Les clés de jambes torsadées (heel hook, torsions du genou) sont réservées aux ceintures marron/noire adulte, principalement en No-Gi.",
              "Slams, projections sur la tête/nuque et toute frappe sont interdits à tous les niveaux.",
              "Les étranglements au collet (arc et flèche, col) existent uniquement en Gi (kimono).",
              "Le comportement antisportif (mordre, tirer les cheveux, plier les doigts, insulter) entraîne la disqualification immédiate.",
              "Chaque compétiteur doit respecter sa catégorie d'âge et de poids officielle (voir divisions ci-dessus).",
            ].map((r, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-white/[0.03] p-4 flex gap-3" data-testid={`rule-${i}`}>
                <ShieldCheck size={16} className="shrink-0 mt-0.5" style={{ color: GOLD }} />
                <p className="text-sm text-white/75 leading-relaxed">{r}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#111] to-black p-8 text-center" data-testid="fqjjb-cta">
          <Trophy size={26} style={{ color: GOLD }} className="mx-auto mb-3" />
          <h3 className="text-2xl font-black" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Prépare tes athlètes à la compétition</h3>
          <p className="text-white/60 mt-2 max-w-xl mx-auto text-sm">Nos Jeux Martiaux inter-écoles et le curriculum KAIZEN suivent ces standards officiels.</p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Link to="/academie" className="bg-[var(--g)] text-black px-6 py-3 font-bold uppercase tracking-wide text-sm hover:bg-white transition-colors" style={{ "--g": GOLD }} data-testid="fqjjb-cta-academie">Découvrir l'Académie</Link>
            <Link to="/cours-essai" className="bg-[#D90429] text-white px-6 py-3 font-bold uppercase tracking-wide text-sm hover:bg-white hover:text-[#D90429] transition-colors" data-testid="fqjjb-cta-essai">Cours d'essai 19,99 $</Link>
          </div>
        </section>
      </main>
    </div>
  );
}
